/**
 * UI Assets Management Page
 *
 * Asset slots are synced via CLI (`devs.sh sync-assets`), not created/deleted here.
 * Admin controls: toggle remote replacement on/off, upload new versions, rollback.
 */

import { useState, useMemo, useRef } from 'react'
import {
  Image, Upload, RotateCcw, ToggleLeft, ToggleRight,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { Modal } from '@/components/common/Modal'
import {
  useUIAssets,
  useUIAssetDetail,
  useUploadUIAsset,
  useRollbackUIAsset,
  useToggleRemote,
} from '@/features/ui-assets'
import type { UiAsset, UiAssetVersion } from '@/types/uiAssets'
import clsx from 'clsx'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_FILE_TYPES = '.png,.jpg,.jpeg,.gif,.webp,.svg'

// ============================================================================
// Helpers
// ============================================================================

function formatSize(bytes: number | null): string {
  if (bytes == null) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString()
}

// ============================================================================
// Remote Toggle Switch (inline, used on cards and detail)
// ============================================================================

function RemoteToggle({
  assetId,
  enabled,
  hasVersion,
  size = 'sm',
}: {
  assetId: string
  enabled: boolean
  hasVersion: boolean
  size?: 'sm' | 'md'
}) {
  const toggleMutation = useToggleRemote()

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation() // Don't trigger card click
    if (!hasVersion && !enabled) {
      toast.error('Upload a version first before enabling remote')
      return
    }
    try {
      await toggleMutation.mutateAsync({ id: assetId, enabled: !enabled })
      toast.success(enabled ? 'Switched to local asset' : 'Remote replacement enabled')
    } catch (err: any) {
      toast.error(err?.message || 'Toggle failed')
    }
  }

  const iconSize = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6'

  return (
    <button
      onClick={handleToggle}
      disabled={toggleMutation.isPending}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
        enabled
          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
        toggleMutation.isPending && 'opacity-50',
      )}
      title={enabled ? 'Using remote asset — click to switch to local' : 'Using local asset — click to enable remote'}
    >
      {enabled ? (
        <ToggleRight className={iconSize} />
      ) : (
        <ToggleLeft className={iconSize} />
      )}
      {enabled ? 'Remote' : 'Local'}
    </button>
  )
}

// ============================================================================
// Asset Card
// ============================================================================

function AssetCard({
  asset,
  onClick,
}: {
  asset: UiAsset
  onClick: () => void
}) {
  return (
    <div
      className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden rounded-lg border border-gray-200 bg-white"
      onClick={onClick}
    >
      {/* Preview */}
      <div className="aspect-video bg-gray-50 flex items-center justify-center overflow-hidden relative">
        {asset.current_url ? (
          <img
            src={asset.current_url}
            alt={asset.name}
            className="w-full h-full object-contain"
          />
        ) : (
          <Image className="w-10 h-10 text-gray-300" />
        )}
        {/* Toggle badge in top-right corner */}
        <div className="absolute top-2 right-2">
          <RemoteToggle
            assetId={asset.id}
            enabled={asset.remote_enabled}
            hasVersion={asset.current_version > 0}
          />
        </div>
      </div>
      {/* Info */}
      <div className="p-3 space-y-1">
        <p className="font-medium text-sm text-gray-900 truncate">{asset.name}</p>
        <p className="text-xs font-mono text-gray-500 truncate">{asset.asset_key}</p>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>v{asset.current_version}</span>
          {asset.file_type && <span>{asset.file_type}</span>}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Detail Panel (Modal)
// ============================================================================

function AssetDetailPanel({
  assetId,
  onClose,
}: {
  assetId: string
  onClose: () => void
}) {
  const { data: detail, isLoading } = useUIAssetDetail(assetId)
  const uploadMutation = useUploadUIAsset()
  const rollbackMutation = useRollbackUIAsset()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum size is 5MB.')
      return
    }

    try {
      await uploadMutation.mutateAsync({ id: assetId, file })
      toast.success('Version uploaded successfully')
    } catch (err) {
      toast.error('Upload failed')
      console.error(err)
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRollback = async (version: number) => {
    try {
      await rollbackMutation.mutateAsync({ id: assetId, version })
      toast.success(`Rolled back to version ${version}`)
    } catch (err) {
      toast.error('Rollback failed')
      console.error(err)
    }
  }

  if (isLoading || !detail) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Asset Detail" size="4xl">
        <div className="flex items-center justify-center py-20 text-gray-400">Loading...</div>
      </Modal>
    )
  }

  const sortedVersions = [...detail.versions].sort((a, b) => b.version - a.version)

  return (
    <Modal isOpen={true} onClose={onClose} title={detail.name} size="4xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Preview + Metadata */}
        <div className="space-y-4">
          <div className="aspect-video bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden border">
            {detail.current_url ? (
              <img
                src={detail.current_url}
                alt={detail.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <Image className="w-16 h-16 text-gray-300" />
            )}
          </div>

          {/* Remote toggle (prominent) */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50">
            <div>
              <p className="text-sm font-medium text-gray-700">Remote Replacement</p>
              <p className="text-xs text-gray-500">
                {detail.remote_enabled
                  ? 'App uses the uploaded remote version'
                  : 'App uses the local bundled asset'}
              </p>
            </div>
            <RemoteToggle
              assetId={assetId}
              enabled={detail.remote_enabled}
              hasVersion={detail.current_version > 0}
              size="md"
            />
          </div>

          {/* Metadata */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Asset Key</span>
              <span className="font-mono">{detail.asset_key}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Group</span>
              <span>{detail.group_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Current Version</span>
              <span>v{detail.current_version}</span>
            </div>
            {detail.file_type && (
              <div className="flex justify-between">
                <span className="text-gray-500">File Type</span>
                <span>{detail.file_type}</span>
              </div>
            )}
            {detail.fallback_asset_path && (
              <div className="flex justify-between">
                <span className="text-gray-500">Fallback Path</span>
                <span className="font-mono text-xs">{detail.fallback_asset_path}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Updated</span>
              <span>{formatDate(detail.updated_at)}</span>
            </div>
          </div>

          {/* Upload action */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              loading={uploadMutation.isPending}
            >
              <Upload className="w-4 h-4 mr-1" />
              Upload New Version
            </Button>
          </div>
        </div>

        {/* Right: Version History */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Version History</h3>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {sortedVersions.map((v) => (
              <VersionEntry
                key={v.id}
                version={v}
                onRollback={() => handleRollback(v.version)}
                isRollingBack={rollbackMutation.isPending}
              />
            ))}
            {sortedVersions.length === 0 && (
              <p className="text-gray-400 text-sm py-8 text-center">
                No versions uploaded yet. Use <code className="bg-gray-100 px-1 rounded">sync-assets</code> or upload manually.
              </p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ============================================================================
// Version Entry
// ============================================================================

function VersionEntry({
  version,
  onRollback,
  isRollingBack,
}: {
  version: UiAssetVersion
  onRollback: () => void
  isRollingBack: boolean
}) {
  return (
    <div
      className={clsx(
        'flex items-center gap-3 p-3 rounded-lg border',
        version.is_active ? 'border-green-200 bg-green-50/50' : 'border-gray-200'
      )}
    >
      <div className="w-14 h-14 flex-shrink-0 rounded bg-gray-100 overflow-hidden flex items-center justify-center">
        {version.url ? (
          <img src={version.url} alt={`v${version.version}`} className="w-full h-full object-cover" />
        ) : (
          <Image className="w-6 h-6 text-gray-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">v{version.version}</span>
          {version.is_active && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
              ACTIVE
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500">{formatDate(version.created_at)}</p>
        <div className="flex gap-3 text-xs text-gray-400">
          {version.file_size != null && <span>{formatSize(version.file_size)}</span>}
          {version.uploaded_by && <span>{version.uploaded_by}</span>}
        </div>
      </div>
      {!version.is_active && (
        <Button
          size="sm"
          variant="outline"
          onClick={onRollback}
          loading={isRollingBack}
          className="flex-shrink-0"
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Rollback
        </Button>
      )}
    </div>
  )
}

// ============================================================================
// Main Page
// ============================================================================

export function UIAssetsPage() {
  const [activeGroup, setActiveGroup] = useState<string>('all')
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)

  const groupParam = activeGroup === 'all' ? undefined : activeGroup
  const { data, isLoading, error } = useUIAssets(groupParam)

  const assets = data?.assets ?? []

  // Derive unique groups from all assets
  const { data: allData } = useUIAssets()
  const groups = useMemo(() => {
    const allAssets = allData?.assets ?? []
    const set = new Set(allAssets.map((a) => a.group_name))
    return Array.from(set).sort()
  }, [allData])

  // Count remote-enabled assets
  const remoteCount = useMemo(() => {
    return (allData?.assets ?? []).filter(a => a.remote_enabled).length
  }, [allData])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Image className="w-6 h-6" />
          UI Assets
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {allData?.total ?? 0} asset slots &middot; {remoteCount} remote-enabled &middot; Sync via <code className="bg-gray-100 px-1 rounded text-xs">devs.sh sync-assets</code>
        </p>
      </div>

      {/* Group filter tabs */}
      {groups.length > 0 && (
        <Tabs value={activeGroup} onValueChange={setActiveGroup}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            {groups.map((g) => (
              <TabsTrigger key={g} value={g}>
                {g}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">Loading...</div>
      ) : error ? (
        <div className="flex items-center justify-center py-20 text-red-500">
          Failed to load assets
        </div>
      ) : assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-2">
          <Image className="w-12 h-12" />
          <p>No assets found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onClick={() => setSelectedAssetId(asset.id)}
            />
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {selectedAssetId && (
        <AssetDetailPanel
          assetId={selectedAssetId}
          onClose={() => setSelectedAssetId(null)}
        />
      )}
    </div>
  )
}
